import dedent from 'dedent';
import * as Geom from '~app/core/twoD';

/*
 * arrowPositions shape:
 * {
 *   paths: {
 *     [flowLabel]: 'path d string',
 *     [`${signalFrom}>${signalTo}`]: 'path d string'
 *   },
 *   arrowHeads: {
 *     [flowLabel | signalfrom>to]: { position: {x, y}, rotation: r }
 *   }
 * }
 */

export const init = state => {
  const { nodes, signals } = state.workingModel;
  state.arrowPositions = {
    paths: {},
    arrowHeads: {},
  };
  Object.keys(nodes)
    .filter(n => nodes[n].type === 'flow')
    .forEach(n => calcFlowArrow(state, n));
  signals.forEach(s => calcSignalArrow(state, s));
};

const FIXED_ANCHOR_RADIUS = 30;

const findAnchorPoint = (state, label, target, offset) => {
  const { nodes } = state.workingModel;
  const { arrowAnchors } = state;
  const node = nodes[label];
  const anchorData = arrowAnchors?.[label];
  if (anchorData) {
    // Look for an intersection of the ray starting at node.position
    // passing through target and any of the line segments in
    // anchorData.lines. There may be more than one, so select the
    // one farthest from node.position.
    let match = null;
    let maxdist = 0;
    for (const seg of anchorData.lines) {
      let seg1 = Geom.add(Geom.vec(seg.x1, seg.y1), node.position);
      let seg2 = Geom.add(Geom.vec(seg.x2, seg.y2), node.position);
      let point = Geom.simpleIntersectRayWithSegment(
        node.position,
        target,
        seg1,
        seg2,
      );
      if (point) {
        const dx = Geom.dist2(node.position, point);
        if (dx > maxdist) {
          match = point;
          maxdist = dx;
        }
      }
    }
    if (match) {
      if (offset !== 0) {
        const unitv = Geom.norm(Geom.sub(match, node.position));
        match = Geom.add(match, Geom.mul(unitv, offset));
      }
      return match;
    }
  }

  // no anchor data or no intersection found; so use a fixed radius from the center
  return Geom.positionAlongRay(node.position, target, FIXED_ANCHOR_RADIUS);
};

const FLOW_ARROWHEAD_LENGTH = 16;
const INFINITY_ICON_RADIUS = 15;
let CONTROLPOINT_DIST_SCALE = 0.4;
const CONTROLPOINT_FLOW_CTR_DIST_SCALE = 0.5;
const CONTROLPOINT_FLOW_END_DIST_SCALE = 0.4;

export const calcFlowArrow = (state, label) => {
  const { nodes } = state.workingModel;
  const { paths, arrowHeads } = state.arrowPositions;
  const { arrowAnchors } = state;
  const anchorData = arrowAnchors?.[label];
  const node = nodes[label];
  const from = node.connectFrom
    ? nodes[node.connectFrom].position
    : node.arrowTailPosition;
  const to = node.connectTo
    ? nodes[node.connectTo].position
    : node.arrowHeadPosition;
  const center = JSON.parse(JSON.stringify(node.position));
  const fromDist = Geom.dist(center, from);
  const toDist = Geom.dist(center, to);

  let UPDATED_CONTROLPOINT_FLOW_END_DIST_SCALE = 0.4;
  // to avoid flipping of the arrowhead
  if (toDist < 100) {
    UPDATED_CONTROLPOINT_FLOW_END_DIST_SCALE = 1.5;
  } else if (toDist < 150) {
    UPDATED_CONTROLPOINT_FLOW_END_DIST_SCALE = 1.0;
  } else if (toDist < 250) {
    UPDATED_CONTROLPOINT_FLOW_END_DIST_SCALE = 0.7;
  } else {
    UPDATED_CONTROLPOINT_FLOW_END_DIST_SCALE = 0.4;
  }

  if (
    !anchorData ||
    !anchorData.flowPoints ||
    anchorData.flowPoints.length < 2
  ) {
    paths[label] = '';
    arrowHeads[label] = null;
    return;
  }
  //debugger;

  const valveLeft = Geom.add(
    anchorData.flowPoints[0].x < anchorData.flowPoints[1].x
      ? anchorData.flowPoints[0]
      : anchorData.flowPoints[1],
    center,
  );
  const valveRight = Geom.add(
    anchorData.flowPoints[0].x > anchorData.flowPoints[1].x
      ? anchorData.flowPoints[0]
      : anchorData.flowPoints[1],
    center,
  );

  let fromOnLeft;
  if (from.x === to.x) {
    fromOnLeft = from.y < to.y;
  } else {
    fromOnLeft = from.x < to.x;
  }
  const leftNode = fromOnLeft ? node.connectFrom : node.connectTo;
  const rightNode = fromOnLeft ? node.connectTo : node.connectFrom;
  const leftDist = fromOnLeft ? fromDist : toDist;
  const rightDist = fromOnLeft ? toDist : fromDist;
  const leftPosition = fromOnLeft ? from : to;
  const rightPosition = fromOnLeft ? to : from;

  // check which side is updating to toDist
  const leftDistEqualsToDist = leftDist === toDist ? true : false;
  const rightDistEqualsToDist = rightDist === toDist ? true : false;

  const lc1 = Geom.sub(valveLeft, {
    x: leftDist * CONTROLPOINT_FLOW_CTR_DIST_SCALE,
    y: 0,
  });
  const rc1 = Geom.add(valveRight, {
    x: rightDist * CONTROLPOINT_FLOW_CTR_DIST_SCALE,
    y: 0,
  });

  const leftOffset = fromOnLeft ? 0 : FLOW_ARROWHEAD_LENGTH;
  const rightOffset = fromOnLeft ? FLOW_ARROWHEAD_LENGTH : 0;

  const leftEnd = leftNode
    ? findAnchorPoint(state, leftNode, lc1, 0 + leftOffset)
    : Geom.positionAlongRay(
        leftPosition,
        lc1,
        INFINITY_ICON_RADIUS + leftOffset,
      );
  const rightEnd = rightNode
    ? findAnchorPoint(state, rightNode, rc1, 0 + rightOffset)
    : Geom.positionAlongRay(
        rightPosition,
        rc1,
        INFINITY_ICON_RADIUS + rightOffset,
      );

  // based on the side which is updating use the correct control point scale
  const lc2 = Geom.positionAlongRay(
    leftPosition,
    leftEnd,
    leftDist *
      (leftDistEqualsToDist
        ? UPDATED_CONTROLPOINT_FLOW_END_DIST_SCALE
        : CONTROLPOINT_FLOW_END_DIST_SCALE),
  );
  const rc2 = Geom.positionAlongRay(
    rightPosition,
    rightEnd,
    rightDist *
      (rightDistEqualsToDist
        ? UPDATED_CONTROLPOINT_FLOW_END_DIST_SCALE
        : CONTROLPOINT_FLOW_END_DIST_SCALE),
  );

  const path = dedent`
    M ${valveLeft.x} ${valveLeft.y}
    C ${lc1.x} ${lc1.y} ${lc2.x} ${lc2.y} ${leftEnd.x} ${leftEnd.y}
    M ${valveRight.x} ${valveRight.y}
    C ${rc1.x} ${rc1.y} ${rc2.x} ${rc2.y} ${rightEnd.x} ${rightEnd.y}
  `;
  const head = {
    position: fromOnLeft ? rightEnd : leftEnd,
    rotation: fromOnLeft
      ? Geom.angleDeg(rc2, rightEnd)
      : Geom.angleDeg(lc2, leftEnd),
  };

  paths[label] = path;
  arrowHeads[label] = head;
};

export const calcSignalArrow = (state, signal) => {
  const { nodes } = state.workingModel;
  const { paths, arrowHeads } = state.arrowPositions;

  const from = nodes[signal.connectFrom].position;
  const to = nodes[signal.connectTo].position;
  const center = signal.position;

  // headings and distances from the center to the ends
  const fromHeading = Geom.angle(center, from);
  const fromDist = Geom.dist(center, from);
  const toHeading = Geom.angle(center, to);
  const toDist = Geom.dist(center, to);

  // to avoid flipping of the arrowhead
  if (toDist < 100) {
    CONTROLPOINT_DIST_SCALE = 0.2;
  } else {
    CONTROLPOINT_DIST_SCALE = 0.4;
  }

  // now find two directions out of the signal handle that are 180 degrees apart and
  // equal angles to both from and to targets
  const bisect = (fromHeading + toHeading) / 2;
  const hdng1 = Geom.normAngle(bisect + Math.PI / 2);
  const hdng2 = Geom.normAngle(bisect - Math.PI / 2);
  let c1Heading = hdng1;
  if (
    Math.abs(Geom.angularDiff(hdng1, fromHeading)) >
    Math.abs(Geom.angularDiff(hdng2, fromHeading))
  ) {
    c1Heading = hdng2;
  }

  const c1 = {
    x: center.x + Math.cos(c1Heading) * (fromDist * CONTROLPOINT_DIST_SCALE),
    y: center.y + Math.sin(c1Heading) * (fromDist * CONTROLPOINT_DIST_SCALE),
  };
  const c2 = {
    x:
      center.x +
      Math.cos(c1Heading + Math.PI) * (toDist * CONTROLPOINT_DIST_SCALE),
    y:
      center.y +
      Math.sin(c1Heading + Math.PI) * (toDist * CONTROLPOINT_DIST_SCALE),
  };

  const start = findAnchorPoint(state, signal.connectFrom, c1, 0);
  const end = findAnchorPoint(state, signal.connectTo, c2, 0);

  const path = dedent`
    M ${start.x} ${start.y}
    Q ${c1.x} ${c1.y} ${center.x} ${center.y}
    Q ${c2.x} ${c2.y} ${end.x} ${end.y}
  `;

  const head = {
    position: end,
    rotation: Geom.angleDeg(c2, end),
  };

  const label = `${signal.connectFrom}>${signal.connectTo}`;
  paths[label] = path;
  arrowHeads[label] = head;
};
